// features/inventory/pages/CountDetailPage.tsx
/**
 * Count Detail Page — Live inventory counting with barcode scanner support.
 *
 * Route: /inventory/counts/:countId
 *
 * Features:
 * - Live counting with inline quantity editing
 * - Barcode scanner mode (USB scanner types into input)
 * - Keyboard shortcuts (Enter to count, Tab to move to next item, Esc to cancel)
 * - Progress dashboard with animated progress bar
 * - Filter by status: all, counted, uncounted, variance
 * - Search by product name
 * - Pagination for large counts (50 per page)
 * - Complete count with confirmation modal
 * - Cancel count with confirmation modal
 * - Variance report view after completion
 * - Visual feedback: green flash on successful count, red for variance
 * - Responsive design for tablet/mobile use during physical counting
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getCountSession,
    updateCountItem,
    completeCountSession,
    cancelCountSession,
    getVarianceReport,
    CountDetail,
    VarianceReport,
} from '../services/inventoryApi';
import {
    ArrowLeft,
    Search,
    X,
    CheckCircle,
    AlertTriangle,
    Package,
    ClipboardList,
    Calculator,
    TrendingUp,
    TrendingDown,
    Minus,
    Play,
    Clock,
    Store,
    Calendar,
    User,
    FileText,
    Barcode,
    Keyboard,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    CheckCheck,
    Trash2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const ITEMS_PER_PAGE = 50;

type ItemFilter = 'all' | 'counted' | 'uncounted' | 'variance';

// ============================================================
// SUB-COMPONENTS
// ============================================================

function ProgressStat({
    label,
    value,
    color,
    pulse,
}: {
    label: string;
    value: string | number;
    color: string;
    pulse?: boolean;
}) {
    return (
        <div className="text-center p-3 bg-slate-50 rounded-xl">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                {label}
            </p>
            <p
                className={clsx(
                    'text-xl font-bold mt-0.5',
                    color,
                    pulse && 'animate-pulse'
                )}
            >
                {value}
            </p>
        </div>
    );
}

function SummaryCard({
    label,
    value,
    color,
    textColor = 'text-slate-900',
}: {
    label: string;
    value: number;
    color: string;
    textColor?: string;
}) {
    return (
        <div
            className={clsx(
                'rounded-2xl border border-slate-200 p-4 text-center',
                color
            )}
        >
            <p className="text-[10px] text-slate-400 uppercase">{label}</p>
            <p className={clsx('text-2xl font-bold mt-1', textColor)}>
                {value}
            </p>
        </div>
    );
}

function VarianceReportView({
    report,
    count,
    onClose,
}: {
    report: VarianceReport;
    count: CountDetail;
    onClose: () => void;
}) {
    const formatNumber = (val: string | undefined | null): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, {
                maximumFractionDigits: 3,
            });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl hover:bg-slate-100"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-xl font-bold">
                                    Variance Report
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {count.countType?.toUpperCase()} Count ·{' '}
                                    {count.branchName}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <SummaryCard
                        label="Total Items"
                        value={report.totalItems}
                        color="bg-slate-50"
                    />
                    <SummaryCard
                        label="Exact Match"
                        value={report.exactMatchItems}
                        color="bg-emerald-50"
                        textColor="text-emerald-700"
                    />
                    <SummaryCard
                        label="Over Count"
                        value={report.overCountItems}
                        color="bg-blue-50"
                        textColor="text-blue-700"
                    />
                    <SummaryCard
                        label="Under Count"
                        value={report.underCountItems}
                        color="bg-rose-50"
                        textColor="text-rose-700"
                    />
                </div>
                <Card>
                    <CardContent className="p-5">
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <p className="text-xs text-slate-400">
                                    Expected Total
                                </p>
                                <p className="text-xl font-bold">
                                    {parseFloat(
                                        report.totalExpectedQuantity
                                    ).toLocaleString()}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400">
                                    Counted Total
                                </p>
                                <p className="text-xl font-bold">
                                    {parseFloat(
                                        report.totalCountedQuantity
                                    ).toLocaleString()}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400">
                                    Net Variance
                                </p>
                                <p
                                    className={clsx(
                                        'text-xl font-bold',
                                        parseFloat(
                                            report.totalVariance
                                        ) >= 0
                                            ? 'text-emerald-600'
                                            : 'text-rose-600'
                                    )}
                                >
                                    {parseFloat(
                                        report.totalVariance
                                    ) >= 0
                                        ? '+'
                                        : ''}
                                    {parseFloat(
                                        report.totalVariance
                                    ).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead>
                                <tr className="bg-slate-50/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                        Product
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Expected
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Counted
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Variance
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        %
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {report.items.map((item) => {
                                    const variance =
                                        parseFloat(
                                            item.variance
                                        );
                                    return (
                                        <tr
                                            key={
                                                item.productId
                                            }
                                            className={clsx(
                                                'hover:bg-slate-50',
                                                Math.abs(
                                                    variance
                                                ) >
                                                0.001 &&
                                                'bg-rose-50/30'
                                            )}
                                        >
                                            <td className="px-4 py-3 text-sm font-medium">
                                                {
                                                    item.productName
                                                }
                                            </td>
                                            <td className="px-4 py-3 text-right text-sm">
                                                {formatNumber(
                                                    item.expectedQuantity
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right text-sm font-medium">
                                                {formatNumber(
                                                    item.countedQuantity
                                                )}
                                            </td>
                                            <td
                                                className={clsx(
                                                    'px-4 py-3 text-right text-sm font-semibold',
                                                    variance >=
                                                        0
                                                        ? 'text-emerald-600'
                                                        : 'text-rose-600'
                                                )}
                                            >
                                                {variance >=
                                                    0
                                                    ? '+'
                                                    : ''}
                                                {variance.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 text-right text-sm text-slate-500">
                                                {
                                                    item.variancePercentage
                                                }
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function CountDetailPage() {
    const navigate = useNavigate();
    const { countId } = useParams<{ countId: string }>();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const barcodeInputRef = useRef<HTMLInputElement>(null);

    // UI State
    const [itemPage, setItemPage] = useState(1);
    const [itemSearch, setItemSearch] = useState('');
    const [itemFilter, setItemFilter] = useState<ItemFilter>('all');
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editQuantity, setEditQuantity] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [barcodeMode, setBarcodeMode] = useState(false);
    const [barcodeValue, setBarcodeValue] = useState('');
    const [quickCountValue, setQuickCountValue] = useState('');
    const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);
    const [showVarianceReport, setShowVarianceReport] = useState(false);
    const [varianceReport, setVarianceReport] =
        useState<VarianceReport | null>(null);
    const [recentlyUpdated, setRecentlyUpdated] = useState<Set<string>>(
        new Set()
    );

    // Queries
    const {
        data: count,
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: [
            'inventory-count-detail',
            countId,
            itemPage,
            itemSearch,
            itemFilter,
        ],
        queryFn: () =>
            getCountSession(
                user!.tenantId,
                countId!,
                itemPage,
                ITEMS_PER_PAGE,
                itemSearch || undefined,
                itemFilter
            ),
        enabled: !!user?.tenantId && !!countId,
        refetchInterval: 10_000,
    });

    // Mutations
    const updateItemMutation = useMutation({
        mutationFn: ({
            itemId,
            data,
        }: {
            itemId: string;
            data: { countedQuantity: string; notes?: string };
        }) =>
            updateCountItem(
                user!.tenantId,
                countId!,
                itemId,
                data
            ),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    'inventory-count-detail',
                    countId,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: ['inventory-counts'],
            });
            queryClient.invalidateQueries({
                queryKey: ['counts-dash'],
            });
            setEditingItemId(null);
            setEditQuantity('');
            setEditNotes('');
            setQuickCountValue('');
            setRecentlyUpdated((prev) =>
                new Set(prev).add(variables.itemId)
            );
            setTimeout(() => {
                setRecentlyUpdated((prev) => {
                    const next = new Set(prev);
                    next.delete(variables.itemId);
                    return next;
                });
            }, 1500);
            if (barcodeMode && barcodeInputRef.current) {
                barcodeInputRef.current.value = '';
                barcodeInputRef.current.focus();
            }
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Update failed',
                message: err?.message,
            });
        },
    });

    const completeMutation = useMutation({
        mutationFn: () =>
            completeCountSession(user!.tenantId, countId!),
        onSuccess: (report) => {
            addToast({
                type: 'success',
                title: 'Count completed',
                message:
                    'Inventory has been reconciled.',
            });
            setShowCompleteConfirm(false);
            setVarianceReport(report);
            setShowVarianceReport(true);
            queryClient.invalidateQueries({
                queryKey: ['inventory-counts'],
            });
            queryClient.invalidateQueries({
                queryKey: ['counts-dash'],
            });
            refetch();
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Failed to complete',
                message: err?.message,
            }),
    });

    const cancelMutation = useMutation({
        mutationFn: () =>
            cancelCountSession(user!.tenantId, countId!),
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Count cancelled',
            });
            setShowCancelConfirm(false);
            queryClient.invalidateQueries({
                queryKey: ['inventory-counts'],
            });
            queryClient.invalidateQueries({
                queryKey: ['counts-dash'],
            });
            navigate('/inventory/counts');
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Failed to cancel',
                message: err?.message,
            }),
    });

    // Derived data
    const items = count?.items || [];
    const pagination = count?.pagination;
    const isActive =
        count?.status === 'draft' || count?.status === 'in_progress';
    const isCompleted = count?.status === 'completed';

    const stats = useMemo(() => {
        const total = count?.totalItems || 0;
        const counted = count?.countedItems || 0;
        const variances = count?.varianceItems || 0;
        const uncounted = total - counted;
        const progressPercent =
            total > 0 ? Math.round((counted / total) * 100) : 0;
        return { total, counted, uncounted, variances, progressPercent };
    }, [count]);

    // Handlers
    const formatNumber = (val: string | undefined | null): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, {
                maximumFractionDigits: 3,
            });
    };

    const formatDate = (dateStr: string | undefined): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const handleBarcodeSubmit = useCallback(() => {
        if (!barcodeValue.trim() || !isActive) return;
        const foundItem = items.find(
            (i) =>
                i.productName
                    ?.toLowerCase()
                    .includes(barcodeValue.toLowerCase()) ||
                i.productId === barcodeValue
        );
        if (foundItem) {
            if (quickCountValue) {
                updateItemMutation.mutate({
                    itemId: foundItem.id,
                    data: {
                        countedQuantity: quickCountValue,
                    },
                });
            } else {
                setEditingItemId(foundItem.id);
                setEditQuantity(
                    foundItem.countedQuantity ||
                    foundItem.expectedQuantity
                );
                setEditNotes(foundItem.notes || '');
            }
        } else {
            addToast({
                type: 'warning',
                title: 'Product not found on this page',
                message:
                    'Try searching or navigating to another page.',
            });
        }
        setBarcodeValue('');
    }, [
        barcodeValue,
        quickCountValue,
        items,
        isActive,
        updateItemMutation,
        addToast,
    ]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;
            if (e.ctrlKey && e.key === 'Enter' && editingItemId) {
                updateItemMutation.mutate({
                    itemId: editingItemId,
                    data: {
                        countedQuantity: editQuantity,
                        notes: editNotes || undefined,
                    },
                });
                return;
            }
            if (e.key === 'Escape' && editingItemId) {
                setEditingItemId(null);
                return;
            }
            if (
                e.key === 'ArrowDown' ||
                (e.key === 'Tab' && !e.shiftKey)
            ) {
                e.preventDefault();
                setFocusedItemIndex((prev) =>
                    Math.min(prev + 1, items.length - 1)
                );
            }
            if (
                e.key === 'ArrowUp' ||
                (e.key === 'Tab' && e.shiftKey)
            ) {
                e.preventDefault();
                setFocusedItemIndex((prev) =>
                    Math.max(prev - 1, 0)
                );
            }
            if (
                e.key === 'Enter' &&
                focusedItemIndex >= 0 &&
                !editingItemId
            ) {
                const item = items[focusedItemIndex];
                if (item) {
                    setEditingItemId(item.id);
                    setEditQuantity(item.countedQuantity || '');
                    setEditNotes(item.notes || '');
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () =>
            window.removeEventListener(
                'keydown',
                handleKeyDown
            );
    }, [
        isActive,
        editingItemId,
        focusedItemIndex,
        items,
        updateItemMutation,
        editQuantity,
        editNotes,
    ]);

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Spinner size="xl" />
            </div>
        );
    }

    // Error state
    if (isError || !count) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorState
                    title="Count not found"
                    message="This count session doesn't exist or you don't have access."
                    onRetry={() =>
                        navigate('/inventory/counts')
                    }
                />
            </div>
        );
    }

    // Variance report view (after completion)
    if (isCompleted && showVarianceReport && varianceReport) {
        return (
            <VarianceReportView
                report={varianceReport}
                count={count}
                onClose={() =>
                    navigate('/inventory/counts')
                }
            />
        );
    }

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate(
                                        '/inventory/counts'
                                    )
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <motion.div
                                whileHover={{ rotate: -5 }}
                                className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25"
                            >
                                <ClipboardList className="w-5 h-5 text-white" />
                            </motion.div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl font-bold text-slate-900">
                                        {isActive
                                            ? 'Live Count'
                                            : 'Count Details'}
                                    </h1>
                                    <Badge
                                        variant={
                                            isActive
                                                ? 'info'
                                                : 'success'
                                        }
                                        size="sm"
                                    >
                                        {isActive && (
                                            <span className="animate-pulse inline-block mr-1">
                                                ●
                                            </span>
                                        )}
                                        {count.status?.replace(
                                            /_/g,
                                            ' '
                                        )}
                                    </Badge>
                                </div>
                                <p className="text-sm text-slate-500">
                                    {count.countType?.toUpperCase()}{' '}
                                    Count ·{' '}
                                    {count.branchName ||
                                        'Warehouse'}{' '}
                                    · {stats.counted}/
                                    {stats.total} counted
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Barcode mode toggle */}
                            {isActive && (
                                <motion.button
                                    whileHover={{
                                        scale: 1.03,
                                    }}
                                    whileTap={{
                                        scale: 0.97,
                                    }}
                                    onClick={() =>
                                        setBarcodeMode(
                                            !barcodeMode
                                        )
                                    }
                                    className={clsx(
                                        'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all shadow-sm',
                                        barcodeMode
                                            ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white'
                                            : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                                    )}
                                    type="button"
                                >
                                    <Barcode className="w-4 h-4" />{' '}
                                    {barcodeMode
                                        ? 'Scanner On'
                                        : 'Scanner'}
                                </motion.button>
                            )}
                            {/* Complete button */}
                            {isActive && (
                                <motion.button
                                    whileHover={{
                                        scale: 1.03,
                                    }}
                                    whileTap={{
                                        scale: 0.97,
                                    }}
                                    onClick={() =>
                                        setShowCompleteConfirm(
                                            true
                                        )
                                    }
                                    disabled={
                                        stats.counted === 0
                                    }
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 text-sm disabled:opacity-50"
                                    type="button"
                                >
                                    <CheckCircle className="w-4 h-4" />{' '}
                                    Complete (
                                    {stats.progressPercent}
                                    %)
                                </motion.button>
                            )}
                            {/* Variance report button for completed */}
                            {isCompleted && (
                                <motion.button
                                    whileHover={{
                                        scale: 1.03,
                                    }}
                                    whileTap={{
                                        scale: 0.97,
                                    }}
                                    onClick={async () => {
                                        const report =
                                            await getVarianceReport(
                                                user!.tenantId,
                                                countId!
                                            );
                                        setVarianceReport(
                                            report
                                        );
                                        setShowVarianceReport(
                                            true
                                        );
                                    }}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg text-sm"
                                    type="button"
                                >
                                    <Calculator className="w-4 h-4" />{' '}
                                    Variance Report
                                </motion.button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Barcode Scanner Bar */}
                <AnimatePresence>
                    {barcodeMode && isActive && (
                        <motion.div
                            initial={{
                                opacity: 0,
                                height: 0,
                            }}
                            animate={{
                                opacity: 1,
                                height: 'auto',
                            }}
                            exit={{
                                opacity: 0,
                                height: 0,
                            }}
                            className="overflow-hidden"
                        >
                            <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 shadow-lg">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <motion.div
                                            animate={{
                                                scale: [
                                                    1,
                                                    1.1,
                                                    1,
                                                ],
                                            }}
                                            transition={{
                                                duration: 1.5,
                                                repeat:
                                                    Infinity,
                                            }}
                                            className="p-2 rounded-xl bg-purple-100"
                                        >
                                            <Barcode className="w-5 h-5 text-purple-600" />
                                        </motion.div>
                                        <div className="flex-1 flex items-center gap-2">
                                            <input
                                                ref={
                                                    barcodeInputRef
                                                }
                                                type="text"
                                                value={
                                                    barcodeValue
                                                }
                                                onChange={(
                                                    e
                                                ) =>
                                                    setBarcodeValue(
                                                        e
                                                            .target
                                                            .value
                                                    )
                                                }
                                                onKeyDown={(
                                                    e
                                                ) => {
                                                    if (
                                                        e.key ===
                                                        'Enter'
                                                    )
                                                        handleBarcodeSubmit();
                                                }}
                                                placeholder="Scan barcode or type product name..."
                                                className="flex-1 px-4 py-2.5 border-2 border-purple-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"
                                                autoFocus
                                            />
                                            <input
                                                type="number"
                                                value={
                                                    quickCountValue
                                                }
                                                onChange={(
                                                    e
                                                ) =>
                                                    setQuickCountValue(
                                                        e
                                                            .target
                                                            .value
                                                    )
                                                }
                                                placeholder="Qty"
                                                onKeyDown={(
                                                    e
                                                ) => {
                                                    if (
                                                        e.key ===
                                                        'Enter'
                                                    )
                                                        handleBarcodeSubmit();
                                                }}
                                                className="w-24 px-3 py-2.5 border-2 border-purple-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"
                                            />
                                            <motion.button
                                                whileHover={{
                                                    scale: 1.05,
                                                }}
                                                whileTap={{
                                                    scale: 0.95,
                                                }}
                                                onClick={
                                                    handleBarcodeSubmit
                                                }
                                                className="px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors text-sm font-medium"
                                                type="button"
                                            >
                                                Count
                                            </motion.button>
                                        </div>
                                        <button
                                            onClick={() =>
                                                setBarcodeMode(
                                                    false
                                                )
                                            }
                                            className="p-2 rounded-xl hover:bg-purple-100 text-purple-500"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-purple-500 mt-2 flex items-center gap-2">
                                        <Keyboard className="w-3 h-3" />{' '}
                                        Enter quantity then
                                        scan, or scan first
                                        then enter quantity.
                                        Press Enter to
                                        confirm.
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Progress Dashboard */}
                <Card>
                    <CardContent className="p-5">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <ProgressStat
                                label="Total Items"
                                value={stats.total}
                                color="text-slate-600"
                            />
                            <ProgressStat
                                label="Counted"
                                value={stats.counted}
                                color="text-emerald-600"
                            />
                            <ProgressStat
                                label="Remaining"
                                value={stats.uncounted}
                                color="text-amber-600"
                                pulse={
                                    stats.uncounted > 0
                                }
                            />
                            <ProgressStat
                                label="Variances"
                                value={stats.variances}
                                color="text-rose-600"
                            />
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                            <motion.div
                                className="bg-gradient-to-r from-emerald-500 to-teal-600 h-3 rounded-full"
                                initial={{ width: 0 }}
                                animate={{
                                    width: `${stats.progressPercent}%`,
                                }}
                                transition={{
                                    duration: 0.8,
                                    ease: 'easeOut',
                                }}
                            />
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                            {count.countedByName && (
                                <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />{' '}
                                    {count.countedByName}
                                </span>
                            )}
                            {count.startedAt && (
                                <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />{' '}
                                    Started{' '}
                                    {formatDate(
                                        count.startedAt
                                    )}
                                </span>
                            )}
                            {count.notes && (
                                <span className="flex items-center gap-1">
                                    <FileText className="w-3 h-3" />{' '}
                                    {count.notes}
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Filters & Search */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by product name..."
                                value={itemSearch}
                                onChange={(e) => {
                                    setItemSearch(
                                        e.target.value
                                    );
                                    setItemPage(1);
                                }}
                                className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                            />
                            {itemSearch && (
                                <button
                                    onClick={() => {
                                        setItemSearch('');
                                        setItemPage(1);
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
                            {([
                                {
                                    value: 'all' as ItemFilter,
                                    label: 'All',
                                    count: stats.total,
                                },
                                {
                                    value: 'counted' as ItemFilter,
                                    label: 'Counted',
                                    count: stats.counted,
                                },
                                {
                                    value: 'uncounted' as ItemFilter,
                                    label: 'Uncounted',
                                    count: stats.uncounted,
                                },
                                {
                                    value: 'variance' as ItemFilter,
                                    label: 'Variance',
                                    count: stats.variances,
                                },
                            ]).map((f) => (
                                <button
                                    key={f.value}
                                    onClick={() => {
                                        setItemFilter(
                                            f.value
                                        );
                                        setItemPage(1);
                                    }}
                                    className={clsx(
                                        'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                                        itemFilter ===
                                            f.value
                                            ? 'bg-white shadow-sm text-slate-700'
                                            : 'text-slate-500'
                                    )}
                                    type="button"
                                >
                                    {f.label} ({f.count})
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Items Table */}
                <Card className="overflow-hidden shadow-sm border border-slate-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead>
                                <tr className="bg-slate-50/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-8">
                                        #
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                        Product
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Expected
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Counted
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Variance
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-20">
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {items.map((item, idx) => {
                                    const isEditing =
                                        editingItemId ===
                                        item.id;
                                    const isCounted =
                                        item.countedQuantity !==
                                        null &&
                                        item.countedQuantity !==
                                        '';
                                    const expected =
                                        parseFloat(
                                            item.expectedQuantity ||
                                            '0'
                                        );
                                    const counted =
                                        parseFloat(
                                            item.countedQuantity ||
                                            '0'
                                        );
                                    const variance = isCounted
                                        ? counted -
                                        expected
                                        : 0;
                                    const globalIdx =
                                        (itemPage - 1) *
                                        ITEMS_PER_PAGE +
                                        idx;
                                    const isFocused =
                                        focusedItemIndex ===
                                        idx;
                                    const justUpdated =
                                        recentlyUpdated.has(
                                            item.id
                                        );

                                    return (
                                        <motion.tr
                                            key={item.id}
                                            initial={{
                                                opacity: 0,
                                            }}
                                            animate={{
                                                opacity: 1,
                                            }}
                                            transition={{
                                                delay:
                                                    idx *
                                                    0.01,
                                            }}
                                            className={clsx(
                                                'group transition-all duration-500',
                                                isFocused &&
                                                'bg-blue-50/50 ring-1 ring-blue-200',
                                                justUpdated &&
                                                'bg-emerald-50/50',
                                                isCounted &&
                                                variance !==
                                                0 &&
                                                'bg-rose-50/20',
                                                isCounted &&
                                                variance ===
                                                0 &&
                                                'bg-emerald-50/10',
                                                'hover:bg-slate-50'
                                            )}
                                        >
                                            <td className="px-4 py-3 text-xs text-slate-400">
                                                {globalIdx +
                                                    1}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <motion.div
                                                        animate={
                                                            justUpdated
                                                                ? {
                                                                    scale: [
                                                                        1,
                                                                        1.3,
                                                                        1,
                                                                    ],
                                                                }
                                                                : {}
                                                        }
                                                        className={clsx(
                                                            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                                                            isCounted
                                                                ? variance ===
                                                                    0
                                                                    ? 'bg-emerald-100'
                                                                    : 'bg-rose-100'
                                                                : 'bg-slate-100'
                                                        )}
                                                    >
                                                        <Package
                                                            className={clsx(
                                                                'w-4 h-4',
                                                                isCounted
                                                                    ? variance ===
                                                                        0
                                                                        ? 'text-emerald-500'
                                                                        : 'text-rose-500'
                                                                    : 'text-slate-400'
                                                            )}
                                                        />
                                                    </motion.div>
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-900">
                                                            {item.productName ||
                                                                'Unknown Product'}
                                                        </p>
                                                        {item.variantName && (
                                                            <p className="text-xs text-slate-400">
                                                                {
                                                                    item.variantName
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right text-sm font-medium tabular-nums">
                                                {formatNumber(
                                                    item.expectedQuantity
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {isEditing &&
                                                    isActive ? (
                                                    <motion.div
                                                        initial={{
                                                            scale: 0.95,
                                                        }}
                                                        animate={{
                                                            scale: 1,
                                                        }}
                                                        className="flex items-center gap-1 justify-end"
                                                    >
                                                        <input
                                                            type="number"
                                                            step="0.001"
                                                            value={
                                                                editQuantity
                                                            }
                                                            onChange={(
                                                                e
                                                            ) =>
                                                                setEditQuantity(
                                                                    e
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                            className="w-28 px-2 py-1.5 border-2 border-emerald-400 rounded-lg text-sm text-right font-medium focus:ring-2 focus:ring-emerald-500/20 bg-white"
                                                            autoFocus
                                                            onKeyDown={(
                                                                e
                                                            ) => {
                                                                if (
                                                                    e
                                                                        .key ===
                                                                    'Enter'
                                                                )
                                                                    updateItemMutation.mutate(
                                                                        {
                                                                            itemId:
                                                                                item.id,
                                                                            data: {
                                                                                countedQuantity:
                                                                                    editQuantity,
                                                                                notes:
                                                                                    editNotes ||
                                                                                    undefined,
                                                                            },
                                                                        }
                                                                    );
                                                                if (
                                                                    e
                                                                        .key ===
                                                                    'Escape'
                                                                )
                                                                    setEditingItemId(
                                                                        null
                                                                    );
                                                            }}
                                                        />
                                                        <motion.button
                                                            whileHover={{
                                                                scale: 1.1,
                                                            }}
                                                            whileTap={{
                                                                scale: 0.9,
                                                            }}
                                                            onClick={() =>
                                                                updateItemMutation.mutate(
                                                                    {
                                                                        itemId:
                                                                            item.id,
                                                                        data: {
                                                                            countedQuantity:
                                                                                editQuantity,
                                                                            notes:
                                                                                editNotes ||
                                                                                undefined,
                                                                        },
                                                                    }
                                                                )
                                                            }
                                                            className="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                                                            type="button"
                                                        >
                                                            <CheckCircle className="w-3.5 h-3.5" />
                                                        </motion.button>
                                                        <motion.button
                                                            whileHover={{
                                                                scale: 1.1,
                                                            }}
                                                            whileTap={{
                                                                scale: 0.9,
                                                            }}
                                                            onClick={() =>
                                                                setEditingItemId(
                                                                    null
                                                                )
                                                            }
                                                            className="p-1.5 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300"
                                                            type="button"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </motion.button>
                                                    </motion.div>
                                                ) : (
                                                    <motion.button
                                                        whileHover={
                                                            isActive
                                                                ? {
                                                                    scale: 1.05,
                                                                }
                                                                : {}
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                !isActive
                                                            )
                                                                return;
                                                            setEditingItemId(
                                                                item.id
                                                            );
                                                            setEditQuantity(
                                                                item.countedQuantity ||
                                                                ''
                                                            );
                                                            setEditNotes(
                                                                item.notes ||
                                                                ''
                                                            );
                                                        }}
                                                        className={clsx(
                                                            'text-sm font-semibold tabular-nums px-3 py-1.5 rounded-lg transition-all',
                                                            isCounted
                                                                ? 'text-emerald-600 bg-emerald-50'
                                                                : 'text-slate-400 hover:text-emerald-500 hover:bg-slate-100',
                                                            !isActive &&
                                                            'cursor-default'
                                                        )}
                                                        type="button"
                                                    >
                                                        {isCounted
                                                            ? formatNumber(
                                                                item.countedQuantity
                                                            )
                                                            : isActive
                                                                ? 'Click to count'
                                                                : '—'}
                                                    </motion.button>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {isCounted && (
                                                    <motion.span
                                                        initial={{
                                                            opacity: 0,
                                                            x: -10,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            x: 0,
                                                        }}
                                                        className={clsx(
                                                            'text-sm font-semibold tabular-nums inline-flex items-center gap-1',
                                                            variance ===
                                                                0
                                                                ? 'text-slate-400'
                                                                : variance >
                                                                    0
                                                                    ? 'text-emerald-600'
                                                                    : 'text-rose-600'
                                                        )}
                                                    >
                                                        {variance ===
                                                            0 ? (
                                                            <Minus className="w-3 h-3" />
                                                        ) : variance >
                                                            0 ? (
                                                            <TrendingUp className="w-3 h-3" />
                                                        ) : (
                                                            <TrendingDown className="w-3 h-3" />
                                                        )}
                                                        {variance ===
                                                            0
                                                            ? '0'
                                                            : `${variance > 0 ? '+' : ''}${formatNumber(item.variance)}`}
                                                    </motion.span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {isCounted ? (
                                                    variance ===
                                                        0 ? (
                                                        <Badge
                                                            variant="success"
                                                            size="sm"
                                                        >
                                                            Match
                                                        </Badge>
                                                    ) : (
                                                        <Badge
                                                            variant="danger"
                                                            size="sm"
                                                        >
                                                            Variance
                                                        </Badge>
                                                    )
                                                ) : (
                                                    <Badge
                                                        variant="neutral"
                                                        size="sm"
                                                    >
                                                        Pending
                                                    </Badge>
                                                )}
                                            </td>
                                        </motion.tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination &&
                        pagination.totalPages > 1 && (
                            <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                                <p className="text-sm text-slate-600">
                                    Showing{' '}
                                    <span className="font-semibold">
                                        {(itemPage - 1) *
                                            ITEMS_PER_PAGE +
                                            1}
                                    </span>{' '}
                                    to{' '}
                                    <span className="font-semibold">
                                        {Math.min(
                                            itemPage *
                                            ITEMS_PER_PAGE,
                                            pagination.totalItems
                                        )}
                                    </span>{' '}
                                    of{' '}
                                    <span className="font-semibold">
                                        {pagination.totalItems.toLocaleString()}
                                    </span>{' '}
                                    items
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() =>
                                            setItemPage(1)
                                        }
                                        disabled={
                                            itemPage ===
                                            1
                                        }
                                        className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30"
                                        type="button"
                                    >
                                        <ChevronsLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setItemPage(
                                                (p) =>
                                                    Math.max(
                                                        1,
                                                        p -
                                                        1
                                                    )
                                            )
                                        }
                                        disabled={
                                            itemPage ===
                                            1
                                        }
                                        className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1"
                                        type="button"
                                    >
                                        <ChevronLeft className="w-4 h-4" />{' '}
                                        Prev
                                    </button>
                                    <span className="px-4 py-2 text-sm font-medium">
                                        Page {itemPage}{' '}
                                        of{' '}
                                        {
                                            pagination.totalPages
                                        }
                                    </span>
                                    <button
                                        onClick={() =>
                                            setItemPage(
                                                (p) =>
                                                    Math.min(
                                                        pagination.totalPages,
                                                        p +
                                                        1
                                                    )
                                            )
                                        }
                                        disabled={
                                            itemPage >=
                                            pagination.totalPages
                                        }
                                        className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1"
                                        type="button"
                                    >
                                        Next{' '}
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setItemPage(
                                                pagination.totalPages
                                            )
                                        }
                                        disabled={
                                            itemPage >=
                                            pagination.totalPages
                                        }
                                        className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30"
                                        type="button"
                                    >
                                        <ChevronsRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}

                    {/* Empty state */}
                    {items.length === 0 && (
                        <div className="text-center py-16">
                            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                                <Package className="w-8 h-8 text-slate-300" />
                            </div>
                            <p className="text-sm text-slate-500">
                                {itemSearch ||
                                    itemFilter !== 'all'
                                    ? 'No items match your filters.'
                                    : 'No items in this count session.'}
                            </p>
                        </div>
                    )}
                </Card>

                {/* Bottom Actions */}
                {isActive && (
                    <div className="flex justify-between">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() =>
                                setShowCancelConfirm(true)
                            }
                            className="flex items-center gap-2 px-5 py-3 border-2 border-rose-200 text-rose-600 rounded-xl hover:bg-rose-50 transition-colors text-sm font-medium"
                            type="button"
                        >
                            <Trash2 className="w-4 h-4" />{' '}
                            Cancel Count
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() =>
                                setShowCompleteConfirm(
                                    true
                                )
                            }
                            disabled={
                                stats.counted === 0
                            }
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 text-sm disabled:opacity-50"
                            type="button"
                        >
                            <CheckCircle className="w-4 h-4" />{' '}
                            Complete Count (
                            {stats.progressPercent}%)
                        </motion.button>
                    </div>
                )}
            </div>

            {/* Complete Confirmation Modal */}
            <AnimatePresence>
                {showCompleteConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8"
                        >
                            <div className="text-center">
                                <motion.div
                                    initial={{
                                        scale: 0,
                                    }}
                                    animate={{
                                        scale: 1,
                                    }}
                                    transition={{
                                        type: 'spring',
                                        delay: 0.1,
                                    }}
                                    className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4"
                                >
                                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                                </motion.div>
                                <h2 className="text-xl font-bold text-slate-900">
                                    Complete Count?
                                </h2>
                                <p className="text-sm text-slate-500 mt-2">
                                    This will finalize the
                                    count and create
                                    adjustment records for
                                    all variances.
                                </p>
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-slate-50 rounded-xl">
                                        <p className="text-xs text-slate-400">
                                            Counted
                                        </p>
                                        <p className="text-xl font-bold text-emerald-600">
                                            {stats.counted}
                                        </p>
                                    </div>
                                    <div className="p-3 bg-amber-50 rounded-xl">
                                        <p className="text-xs text-amber-600">
                                            Uncounted
                                        </p>
                                        <p className="text-xl font-bold text-amber-700">
                                            {
                                                stats.uncounted
                                            }
                                        </p>
                                    </div>
                                    <div className="p-3 bg-rose-50 rounded-xl">
                                        <p className="text-xs text-rose-600">
                                            Variances
                                        </p>
                                        <p className="text-xl font-bold text-rose-700">
                                            {
                                                stats.variances
                                            }
                                        </p>
                                    </div>
                                    <div className="p-3 bg-emerald-50 rounded-xl">
                                        <p className="text-xs text-emerald-600">
                                            Progress
                                        </p>
                                        <p className="text-xl font-bold text-emerald-700">
                                            {
                                                stats.progressPercent
                                            }
                                            %
                                        </p>
                                    </div>
                                </div>
                                {stats.uncounted > 0 && (
                                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-700">
                                            {
                                                stats.uncounted
                                            }{' '}
                                            item(s) have not
                                            been counted. They
                                            will be treated as
                                            matching expected
                                            quantities.
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() =>
                                        setShowCompleteConfirm(
                                            false
                                        )
                                    }
                                    className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-white transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <motion.button
                                    whileHover={{
                                        scale: 1.02,
                                    }}
                                    whileTap={{
                                        scale: 0.98,
                                    }}
                                    onClick={() =>
                                        completeMutation.mutate()
                                    }
                                    disabled={
                                        completeMutation.isPending
                                    }
                                    className="flex-1 px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    type="button"
                                >
                                    {completeMutation.isPending ? (
                                        <Spinner
                                            size="xs"
                                            variant="white"
                                        />
                                    ) : (
                                        <CheckCheck className="w-4 h-4" />
                                    )}{' '}
                                    Complete Count
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Cancel Confirmation Modal */}
            <AnimatePresence>
                {showCancelConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center"
                        >
                            <div className="w-14 h-14 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-7 h-7 text-rose-500" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900">
                                Cancel This Count?
                            </h3>
                            <p className="text-sm text-slate-500 mt-2">
                                This action cannot be undone.
                                All counting progress will be
                                lost.
                            </p>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() =>
                                        setShowCancelConfirm(
                                            false
                                        )
                                    }
                                    className="flex-1 px-4 py-2.5 border-2 border-slate-300 rounded-xl font-medium text-sm"
                                    type="button"
                                >
                                    Go Back
                                </button>
                                <motion.button
                                    whileHover={{
                                        scale: 1.02,
                                    }}
                                    whileTap={{
                                        scale: 0.98,
                                    }}
                                    onClick={() =>
                                        cancelMutation.mutate()
                                    }
                                    disabled={
                                        cancelMutation.isPending
                                    }
                                    className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl font-semibold text-sm hover:bg-rose-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    type="button"
                                >
                                    {cancelMutation.isPending ? (
                                        <Spinner
                                            size="xs"
                                            variant="white"
                                        />
                                    ) : (
                                        'Cancel Count'
                                    )}
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}